import React, { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import Layout from './components/Layout';
import { config } from './config/environment';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import AnalyticsPage from './pages/AnalyticsPage';
import ApiLogsPage from './pages/ApiLogsPage';
import BlogEditorPage from './pages/BlogEditorPage';
import BlogPage from './pages/BlogPage';
import CouponsPage from './pages/CouponsPage';
import DashboardPage from './pages/DashboardPage';
import InventoryPage from './pages/InventoryPage';
import InvoicesPage from './pages/InvoicesPage';
import LoginPage from './pages/LoginPage';
import MiscCostsPage from './pages/MiscCostsPage';
import OrderDetailPage from './pages/OrderDetailPage';
import OrdersPage from './pages/OrdersPage';
import PackagingPage from './pages/PackagingPage';
import PricingPage from './pages/PricingPage';
import ProductsPage from './pages/ProductsPage';
import ReviewsPage from './pages/ReviewsPage';
import TestimonialsPage from './pages/TestimonialsPage';
import UserDetailPage from './pages/UserDetailPage';
import UsersPage from './pages/UsersPage';
import WaitlistPage from './pages/WaitlistPage';
import WalletPage from './pages/WalletPage';
import BroadcastsPage from './pages/BroadcastsPage';
import WhatsappMessagesPage from './pages/WhatsappMessagesPage';

function RequireAdmin({ children }: { children: React.ReactElement }) {
  const { token, isAdmin } = useAuth();
  if (!token || !isAdmin) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  // Make browser tabs distinguishable when prod and UAT are open side by side
  useEffect(() => {
    document.title = config.appName;
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              element={
                <RequireAdmin>
                  <Layout />
                </RequireAdmin>
              }
            >
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/reviews" element={<ReviewsPage />} />
              <Route path="/testimonials" element={<TestimonialsPage />} />
              <Route path="/blog" element={<BlogPage />} />
              {/* /new before /:id/edit — both are two segments under /blog, and a
                  literal segment must be matched before the parameterised one. */}
              <Route path="/blog/new" element={<BlogEditorPage />} />
              <Route path="/blog/:id/edit" element={<BlogEditorPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/orders/:id" element={<OrderDetailPage />} />
              <Route path="/inventory" element={<InventoryPage />} />
              <Route path="/packaging" element={<PackagingPage />} />
              <Route path="/misc-costs" element={<MiscCostsPage />} />
              <Route path="/invoices" element={<InvoicesPage />} />
              <Route path="/coupons" element={<CouponsPage />} />
              <Route path="/wallet" element={<WalletPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/users/:id" element={<UserDetailPage />} />
              <Route path="/waitlist" element={<WaitlistPage />} />
              <Route path="/broadcasts" element={<BroadcastsPage />} />
              <Route path="/whatsapp-messages" element={<WhatsappMessagesPage />} />
              <Route path="/api-logs" element={<ApiLogsPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
